import { type ComputedRef, type Ref, watch } from 'vue'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'

interface UseProductListingPage {
    breadcrumbs: ComputedRef<Breadcrumb[]>
}

type Facet = {
    totalResults: number
} & Record<string, any>

const predicate = (facet: Facet | undefined | null): boolean =>
    facet === undefined || (facet?.totalResults ?? 0) > 0

export function useProductListingPage<
    const F extends Record<string, Facet | Facet[] | undefined | null>,
>({
    facets,
    resetRouteGetter,
    breadcrumbsGetter,
}: {
    facets: Readonly<Ref<F>>
    resetRouteGetter: (facets: F) => RouteLocationRaw
    breadcrumbsGetter: (facets: Readonly<Ref<F>>) => ComputedRef<Breadcrumb[]>
}): UseProductListingPage {
    const redirect = useRedirect()
    const router = useRouter()

    watch(
        facets,
        async (newFacets) => {
            const newFacetsEntries = Object.entries(newFacets)
            const goodFacetsEntries = newFacetsEntries.filter((facet) => {
                const value = facet[1]

                if (Array.isArray(value)) {
                    return predicate({
                        totalResults: value.reduce(
                            (acc, item) => acc + item.totalResults,
                            0
                        ),
                    })
                }

                return predicate(value)
            })

            if (goodFacetsEntries.length === newFacetsEntries.length) {
                return
            }

            const href = router.resolve(
                resetRouteGetter(Object.fromEntries(goodFacetsEntries) as F)
            ).href

            await redirect(href, 301)
        },
        { immediate: true }
    )

    return {
        breadcrumbs: breadcrumbsGetter(facets),
    }
}
